import { createSimState, type SimState } from './state'
import { assignLabor, enqueue, spawnColonist } from './commands'
import type { Content, ContentId } from '../content/schema'

// The M0 fun-gate scene: N square-colonists hauling through one or more parallel
// mine → smelter → storage chains, built entirely on the real headless core.
// Colonists enter only via spawn_colonist commands — never placed into
// createSimState — so all mutation flows through the queue. Spawn positions are
// fixed sim-world coordinates (never viewport-derived), so the same options and
// seed always produce the same scene.

/** Default colonist count: enough surplus that queuing and pooling are visible. */
export const DEFAULT_COLONIST_COUNT = 24

// Colonists spawn on a grid in the top-right, clear of the buildings and far
// from the pool rally point at the top-left, so idle colonists visibly travel to
// pool and haulers visibly travel to the chain.
const SPAWN_ORIGIN_X = 700
const SPAWN_ORIGIN_Y = 20
const SPAWN_STRIDE = 12
const SPAWN_COLS = 6

export interface M0SceneOpts {
  colonistCount?: number
  groupCount?: number
  masterSeed?: number
}

function spawnPos(index: number): { x: number; y: number } {
  const col = index % SPAWN_COLS
  const row = Math.floor(index / SPAWN_COLS)
  return {
    x: SPAWN_ORIGIN_X + col * SPAWN_STRIDE,
    y: SPAWN_ORIGIN_Y + row * SPAWN_STRIDE,
  }
}

/**
 * Build the M0 scene: lay out `groupCount` parallel chains and queue spawn
 * commands for `colonistCount` colonists. The commands sit on the queue; the
 * first tick drains them (units enter via the command queue).
 */
export function createM0Scene(
  content: Content,
  chainIds: ContentId[],
  opts: M0SceneOpts = {},
): SimState {
  const state = createSimState(content, chainIds, {
    masterSeed: opts.masterSeed,
    groupCount: opts.groupCount,
  })
  const colonistCount = opts.colonistCount ?? DEFAULT_COLONIST_COUNT
  for (let i = 0; i < colonistCount; i++) {
    enqueue(state.commandQueue, spawnColonist(i, spawnPos(i)))
  }
  // Assign the colonists across the chains so the M0 scene runs without hand
  // input: chains default to a zero target (a chain moves nothing until
  // assigned), so distribute the pool evenly (earliest chains take the
  // remainder). The arbiter draws from the one shared pool up to each target; a
  // chain that can't use all its assigned labor leaves the surplus idle at the
  // rally point, exactly as a hand-assigned chain would.
  const chainCount = state.chains.length
  for (let g = 0; g < chainCount; g++) {
    const base = Math.floor(colonistCount / chainCount)
    const extra = g < colonistCount % chainCount ? 1 : 0
    enqueue(state.commandQueue, assignLabor(g, base + extra))
  }
  return state
}
